import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { SafeImage } from '@/components/safe-image.tsx';
import { Header } from '@/components/header.tsx';
import { TabBar } from '@/components/tab-bar.tsx';
import { MSG } from '@/lib/constants.ts';
import {
  POINT_EXPIRE_YEARS,
  WITHDRAW_MAXIMUM_POINT,
  WITHDRAW_MINIMUM_POINT,
} from '@/data/policy.ts';
import { MEMBER_GRADE_LABEL, SOCIAL_PROVIDERS } from '@/data/social.ts';
import { bankLogo } from '@/data/withdraw.ts';
import { fetchMypage } from '@/lib/user.api.ts';
import { cancelWithdraw } from '@/lib/withdraw.api.ts';
import { formatPrice, returnUrl } from '@/lib/utils.ts';
import { logout, signIn } from '@/store/auth.slice.ts';
import { askAlert, askConfirm } from '@/store/dialog.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import type { PageProps } from '@/types/props.ts';
import type { WithdrawAccount, WithdrawItem } from '@/types/withdraw.ts';

/** 마이페이지가 함께 받아 오는 것들 (백엔드 `mypage()` 응답) */
interface MypageExtra {
  /** 이번 달 안에 사라지는 포인트 */
  expiring: number;
  /** 운영 공지 한 줄. 없으면 null */
  notice: { content: string; href: string | null } | null;
  account: WithdrawAccount | null;
  pending: WithdrawItem | null;
}

const Mypage: FunctionalComponent<PageProps> = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const hydrated = useAppSelector((state) => state.auth.hydrated);

  const [extra, setExtra] = useState<MypageExtra | null>(null);
  const [busy, setBusy] = useState(false);
  /** 포인트 안내 말풍선 */
  const [tipOpen, setTipOpen] = useState(false);
  /** 진행중 신청 팝업 */
  const [pendingOpen, setPendingOpen] = useState(false);
  /**
   * 출금 신청을 막 끝내고 돌아왔는가.
   *
   * 신청 화면이 `/mypage` 로 되돌려 보내면서 알림을 띄운다(시안 `success-alert`).
   */
  const [justRequested, setJustRequested] = useState(false);

  const userSeq = user?.userSeq;

  useEffect(() => {
    if (!hydrated) return;
    if (!user?.id) route(`/login?r=${encodeURIComponent(returnUrl())}`, true);
  }, [hydrated, user?.id]);

  useEffect(() => {
    if (!userSeq) return;

    let cancelled = false;

    void (async () => {
      try {
        const response = await fetchMypage(userSeq);
        if (cancelled) return;

        if (!response.result) {
          await dispatch(askAlert(response.message ?? MSG.apiFail));
          return;
        }

        const data = response.data as (typeof response.data & Partial<MypageExtra>) | undefined;
        setExtra({
          expiring: data?.expiring ?? 0,
          notice: data?.notice ?? null,
          account: data?.account ?? null,
          pending: data?.pending ?? null,
        });
        // 신청 직후 돌아오면 잠깐 알림을 띄운다.
        if (data?.pending) setJustRequested(sessionStorage.getItem('withdraw.requested') === '1');
        sessionStorage.removeItem('withdraw.requested');

        // 서버가 돌려준 최신 회원 정보(포인트 잔액 포함)로 세션까지 갱신한다.
        // ★ 병합({...user, ...next})이 아니라 교체다.
        //   백엔드는 값이 NULL 인 필드를 응답에서 **아예 빼고** 보낸다(null 로 보내지 않음).
        //   병합하면 서버에서 지워진 값(예: 마케팅 동의 철회)이 세션의 옛 값으로 되살아나
        //   영영 꺼지지 않는다. 회원 정보는 서버가 정본이므로 통째로 갈아끼운다.
        const next = response.data?.user;
        if (next) dispatch(signIn(next));
      } catch {
        if (!cancelled) await dispatch(askAlert(MSG.apiFail));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userSeq, dispatch]);

  function withdrawHandler() {
    // 진행중 신청이 있으면 새로 신청할 수 없다. 그 내용을 보여준다.
    if (pending) {
      setPendingOpen(true);
      return;
    }
    if (!canWithdraw) return;
    route('/mypage/withdraw');
  }

  /** 진행중 신청 취소 — 차감된 포인트가 되돌아온다 */
  async function cancelPending() {
    if (!pending || busy) return;
    if (!(await dispatch(askConfirm('출금 신청을 취소하시겠습니까?')))) return;

    setBusy(true);
    try {
      const response = await cancelWithdraw(Number(userSeq), pending.withdrawSeq);
      if (!response.result) {
        await dispatch(askAlert(response.message ?? MSG.apiFail));
        return;
      }

      setPendingOpen(false);
      // 포인트와 진행중 신청이 함께 바뀐다. 서버가 준 값으로 다시 맞춘다.
      const refreshed = await fetchMypage(Number(userSeq));
      if (refreshed.result) {
        const data = refreshed.data as (typeof refreshed.data & Partial<MypageExtra>) | undefined;
        if (data?.user) dispatch(signIn(data.user));
        setExtra({
          expiring: data?.expiring ?? 0,
          notice: data?.notice ?? null,
          account: data?.account ?? null,
          pending: data?.pending ?? null,
        });
      }
      await dispatch(askAlert('출금 신청을 취소했습니다.'));
    } catch {
      await dispatch(askAlert(MSG.apiFail));
    } finally {
      setBusy(false);
    }
  }

  async function logoutHandler() {
    if (!(await dispatch(askConfirm('로그아웃하시겠습니까?')))) return;

    void dispatch(logout());
    route('/', true);
  }

  const badge = user?.site ? SOCIAL_PROVIDERS[user.site] : null;
  const remainPoint = user?.requestDetailPoint ?? 0;

  // 최소 금액에 못 미치면 출금 버튼을 잠근다. 안내는 말풍선이 한다.
  const point = user?.point ?? 0;
  const canWithdraw = point >= WITHDRAW_MINIMUM_POINT;

  const expiring = extra?.expiring ?? 0;
  const notice = extra?.notice ?? null;
  const account = extra?.account ?? null;
  const pending = extra?.pending ?? null;

  return (
    <div class="page">
      <Header />

      <div class="section">
        <div class="mypage">
          <div class="my-header">
            <div class="my-header-top">
              <div class="user-name">
                <span>{user?.nickname || user?.name || '회원'}</span>님, 안녕하세요
              </div>
              <a
                href="/mypage/edit"
                class="settings-btn"
                aria-label="회원정보 수정"
                onClick={(e) => {
                  e.preventDefault();
                  route('/mypage/edit');
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </a>
            </div>

            {badge && (
              <div class="user-meta">
                <div
                  class="kakao-icon"
                  style={badge.background ? { background: badge.background } : undefined}
                >
                  <img src={badge.icon} alt="" />
                </div>
                {badge.label} · {MEMBER_GRADE_LABEL}
              </div>
            )}
          </div>

          {/* 출금 신청을 막 끝내고 돌아왔을 때만 */}
          {justRequested && (
            <div class="success-alert">
              <span class="success-alert-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M8 12.5l2.5 2.5L16 9" />
                </svg>
              </span>
              <span class="success-alert-text">출금 신청이 완료되었습니다.</span>
            </div>
          )}

          {/* 운영 공지 — 관리 화면에서 고친다 (MC_NOTICE) */}
          {notice && (
            <div
              class="admin-notice"
              onClick={() => {
                if (notice.href) route(notice.href);
              }}
            >
              <span class="admin-notice-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M7.33325 4.66658H8.66658V5.99992H7.33325V4.66658ZM7.33325 7.33325H8.66658V11.3333H7.33325V7.33325ZM7.99992 1.33325C4.31992 1.33325 1.33325 4.31992 1.33325 7.99992C1.33325 11.6799 4.31992 14.6666 7.99992 14.6666C11.6799 14.6666 14.6666 11.6799 14.6666 7.99992C14.6666 4.31992 11.6799 1.33325 7.99992 1.33325ZM7.99992 13.3333C5.05992 13.3333 2.66659 10.9399 2.66659 7.99992C2.66659 5.05992 5.05992 2.66659 7.99992 2.66659C10.9399 2.66659 13.3333 5.05992 13.3333 7.99992C13.3333 10.9399 10.9399 13.3333 7.99992 13.3333Z"
                    fill="#F56074"
                  />
                </svg>
              </span>
              <span class="admin-notice-text">{notice.content}</span>
            </div>
          )}

          {/* 추가정보를 더 입력하면 받을 수 있는 포인트가 남았을 때만 */}
          {remainPoint > 0 && (
            <div
              class="point-banner"
              onClick={() => route(`/info?r=${encodeURIComponent(returnUrl())}`)}
            >
              <span class="banner-text">
                지금 적립 가능한 <strong>{formatPrice(remainPoint)}P</strong> 확인하기
              </span>
            </div>
          )}

          <div class="point-card">
            <div class="card-label">
              내 보유 포인트
              <span class="info-tooltip-wrap">
                <button
                  type="button"
                  class="info-tooltip-btn"
                  aria-label="포인트 안내"
                  onClick={() => setTipOpen((v) => !v)}
                >
                  ?
                </button>
                {tipOpen && (
                  <span class="info-tooltip">
                    최소 {formatPrice(WITHDRAW_MINIMUM_POINT)}P~ 최대{' '}
                    {formatPrice(WITHDRAW_MAXIMUM_POINT)}P까지 출금 가능하며, 유효기간은{' '}
                    {POINT_EXPIRE_YEARS}년입니다.
                    <button
                      type="button"
                      class="info-tooltip-close"
                      aria-label="닫기"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTipOpen(false);
                      }}
                    >
                      ✕
                    </button>
                  </span>
                )}
              </span>
            </div>

            {/* 포인트 내역은 별도 화면으로 옮겼다. 금액을 누르면 그리로 간다. */}
            <div class="card-amount" onClick={() => route('/mypage/history')}>
              <div class="coin-icon">
                <img src="/images/point.png" alt="" />
              </div>
              <div class="amount-value">{formatPrice(point)}</div>
              <span class="amount-arrow">
                <img src="/images/arrow-right.png" alt="" />
              </span>
            </div>

            <button
              type="button"
              class="withdraw-btn"
              disabled={!canWithdraw && !pending}
              onClick={withdrawHandler}
            >
              {pending ? '신청 내역 보기' : '출금 신청하기'}
            </button>

            <div class="expiring-point-row">
              <span class="expiring-point-label">이번 달 소멸 예정 포인트</span>
              <span class="expiring-point-value">{formatPrice(expiring)}P</span>
            </div>
          </div>

          {/* 출금 계좌 */}
          <div class="withdraw-account-section">
            <div class="withdraw-account-title">포인트 출금 계좌</div>
            <div class="withdraw-account-row">
              {account ? (
                <div class="withdraw-account-info">
                  <span class="withdraw-account-bank-icon">
                    {bankLogo(account.bankName) && (
                      <SafeImage src={bankLogo(account.bankName)} alt={account.bankName ?? ''} />
                    )}
                  </span>
                  <span class="withdraw-account-detail">
                    <span class="withdraw-account-number">
                      {account.bankName}({account.accountTail})
                    </span>
                    <span class="withdraw-account-name">{account.holder}</span>
                  </span>
                </div>
              ) : (
                <span class="withdraw-account-empty">등록된 출금계좌가 없습니다.</span>
              )}
              <button
                type="button"
                class="withdraw-account-register-btn"
                onClick={() => route('/mypage/account')}
              >
                {account ? '변경하기' : '등록하기'}
              </button>
            </div>
          </div>
        </div>

        <div class="logout-section">
          <button type="button" class="logout-btn" onClick={logoutHandler}>
            로그아웃
          </button>
        </div>
      </div>

      {/* 이미 진행중인 신청 (새 시안) */}
      <div class={pendingOpen ? 'popup-overlay show' : 'popup-overlay'}>
        <div class="popup-sheet">
          <div class="popup-body popup-body--pending">
            <span class="popup-icon">
              <img src="/images/warning.png" alt="" />
            </span>
            <h3 class="popup-title">
              이미 진행중인
              <br />
              신청건이 있습니다.
            </h3>

            <div class="popup-detail-rows">
              <div class="popup-detail-row">
                <span class="popup-detail-label">입금 계좌</span>
                <span class="popup-detail-value">
                  {pending?.bankName}({pending?.accountTail})
                </span>
              </div>
              <div class="popup-detail-row">
                <span class="popup-detail-label">입금 예정액</span>
                <span class="popup-detail-value">{formatPrice(pending?.amount ?? 0)}원</span>
              </div>
              <div class="popup-detail-row">
                <span class="popup-detail-label">입금 예정일</span>
                <span class="popup-detail-value">{pending?.expectDate ?? '-'} 이내</span>
              </div>
            </div>
          </div>

          <div class="popup-footer-row">
            <button
              type="button"
              class="popup-cancel-btn"
              disabled={busy}
              onClick={() => void cancelPending()}
            >
              신청 취소
            </button>
            <button type="button" class="popup-confirm-btn" onClick={() => setPendingOpen(false)}>
              확인
            </button>
          </div>
        </div>
      </div>

      <TabBar path="/mypage" />
    </div>
  );
};

export default Mypage;
